import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Copy } from 'lucide-react';
import { adminApi } from '../../lib/api-client';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input, Label, FieldHint } from '../../components/ui/input';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { friendlyErrorMessage } from '../../components/ui/states';

/** `name` -> a valid workspace slug (lowercase, alphanumeric + hyphens only — the same charset
 * the backend's `createWorkspaceSchema` enforces). Only ever used as an initial suggestion: the
 * slug field stays independently editable, and stops auto-updating the moment a caller edits it
 * directly (see `slugTouched` below). */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function RevealTempPassword({ password, ownerEmail, onDone }: { password: string; ownerEmail: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        Temporary password for <span className="font-medium text-ink">{ownerEmail}</span>.
      </p>
      <p className="text-sm font-medium text-danger">Share this securely — it will not be shown again.</p>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{password}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <Button className="w-full" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

export function NewWorkspace() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ temporaryPassword: string; ownerEmail: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => adminApi.createWorkspace({ name, slug, ownerEmail, ownerName }),
    onSuccess: (created) => setResult({ temporaryPassword: created.temporaryPassword, ownerEmail: created.ownerEmail }),
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <div>
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink">
        <ArrowLeft size={14} /> Back to Admin Console
      </Link>

      <PageHeader title="New Workspace" description="Create a new customer workspace and its first Owner." />

      <Card className="max-w-lg">
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1">
              <Label htmlFor="workspace-name">Workspace name</Label>
              <Input id="workspace-name" value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="Acme Corporation" required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="workspace-slug">Slug</Label>
              <Input
                id="workspace-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="acme-corporation"
                pattern="[a-z0-9-]+"
                required
              />
              <FieldHint>Lowercase letters, numbers, and hyphens only. Used in login URLs.</FieldHint>
            </div>
            <div className="space-y-1">
              <Label htmlFor="owner-email">Owner email</Label>
              <Input id="owner-email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@acme.com" required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="owner-name">Owner full name</Label>
              <Input id="owner-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Jane Doe" required />
            </div>
            {error ? <p className="text-sm text-danger">{error}</p> : null}
            <Button
              type="submit"
              className="w-full"
              disabled={mutation.isPending || !name.trim() || !slug.trim() || !ownerEmail.trim() || !ownerName.trim()}
            >
              Create workspace
            </Button>
          </form>
        </CardContent>
      </Card>

      <Dialog open={result !== null} onOpenChange={(open) => !open && navigate('/admin')}>
        <DialogContent title="Workspace created">
          {result ? (
            <RevealTempPassword password={result.temporaryPassword} ownerEmail={result.ownerEmail} onDone={() => navigate('/admin')} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
